import * as React from 'react';
import { TablePrimitiveCell, TablePrimitiveRow } from '@components/organisms';
import { cn } from '@utils';
import { resolveDataGridCellMaxWidth, resolveDataGridCellWidth } from './cell-width';
import type { IDataGridColumnLike } from './data-grid-column-like.type';

interface IDataGridSkeletonRowsProps {
	columns: IDataGridColumnLike[];
	rowCount: number;
	rowHeight?: string;
	rowSelection?: 'single' | 'multiple';
}

export const DataGridSkeletonRows = ({ columns, rowCount, rowHeight, rowSelection }: IDataGridSkeletonRowsProps) => {
	const lastColumnId = columns[columns.length - 1]?.id;

	return (
		<React.Fragment>
			{Array.from({ length: rowCount }).map((_, rowIndex) => (
				<TablePrimitiveRow
					key={`skeleton-row-${rowIndex}`}
					style={{ minHeight: rowHeight, height: rowHeight }}
				>
					{columns.map((column, columnIndex) => {
						const isFirstColumn = columnIndex === 0;
						const isLastColumn = columnIndex === columns.length - 1;
						const isActionsColumn = isLastColumn && lastColumnId === 'actions';
						const isSelectionColumn = isFirstColumn && Boolean(rowSelection);
						const fixedColumnStyle = resolveDataGridCellWidth({
							isFirstColumn,
							isActionsColumn,
							hasRowSelection: Boolean(rowSelection),
							hasExplicitWidth: column.meta?.hasExplicitWidth ?? false,
							size: column.size,
						});
						const maxWidth = resolveDataGridCellMaxWidth(fixedColumnStyle);

						return (
							<TablePrimitiveCell
								key={`skeleton-cell-${column.id ?? columnIndex}`}
								data-testid="data-grid-skeleton-cell"
								maxWidth={maxWidth}
								overflow="hidden"
								style={fixedColumnStyle}
							>
								{isActionsColumn ? (
									<div className={cn('flex w-full justify-end')}>
										<div className={cn('h-4 w-4 animate-pulse rounded bg-disabled')} />
									</div>
								) : (
									<div
										className={cn(
											'animate-pulse rounded bg-disabled',
											isSelectionColumn ? 'h-4 w-4' : 'h-3 w-full max-w-[140px]'
										)}
									/>
								)}
							</TablePrimitiveCell>
						);
					})}
				</TablePrimitiveRow>
			))}
		</React.Fragment>
	);
};
